import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { Edit } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GameQueryKeys } from '@/modules/game/keys'
import { GameProviderWithBannerListTableData } from '@/modules/game/type'
import useMutationCreateProviderBannerFileUrl from '@/modules/game/api/hooks/useMutationCreateProviderBannerFileUrl'
import UploadImage from '@/common/components/UploadImage'
import useMutationUpdateGameTypeProvider from '@/modules/game/api/hooks/useMutationUpdateGameTypeProvider'

interface EditProviderBannerProps {
	row: GameProviderWithBannerListTableData
}

export default function EditProviderBanner({ row }: EditProviderBannerProps) {
	const [open, setOpen] = useState(false)
	const [bannerUrl, setBannerUrl] = useState(row.bannerUrl || '')
	const { mutate: updateGameTypeProvider } = useMutationUpdateGameTypeProvider()
	const { mutateAsync: getProviderBannerFileUrl } =
		useMutationCreateProviderBannerFileUrl()
	const queryClient = useQueryClient()

	const handleImageUploaded = (fileUrl: string) => {
		setBannerUrl(fileUrl)
	}

	const handleSave = () => {
		if (!bannerUrl) {
			showErrorToast('กรุณาเลือกรูปภาพแบนเนอร์')
			return
		}

		updateGameTypeProvider(
			{
				id: row.id,
				payload: {
					bannerUrl: bannerUrl,
				},
			},
			{
				onSuccess: () => {
					toast.success('อัพเดทแบนเนอร์สำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [GameQueryKeys.GetGameProviderList],
					})
					setOpen(false)
				},
				onError: (error) => {
					showErrorToast(error.message || 'เกิดข้อผิดพลาด')
				},
			},
		)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="ghost" size="sm">
					<Edit className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>แก้ไขแบนเนอร์ {row.displayName}</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div className="space-y-2">
						<label className="text-sm font-medium">แบนเนอร์ปัจจุบัน</label>
						<div className="flex items-center justify-center rounded border bg-gray-50 p-4">
							{bannerUrl ? (
								<img
									src={bannerUrl}
									alt={`${row.displayName} banner`}
									className="h-32 w-full rounded object-cover"
								/>
							) : (
								<div className="flex h-32 w-full items-center justify-center text-gray-500">
									ไม่มีแบนเนอร์
								</div>
							)}
						</div>
					</div>
					<div className="space-y-2">
						<label className="text-sm font-medium">
							อัพโหลดแบนเนอร์ใหม่{' '}
							<span className="text-amber-600 dark:text-amber-400">
								(สัดส่วน 350:145)
							</span>
						</label>
						<UploadImage
							onImageUploaded={handleImageUploaded}
							defaultValue={bannerUrl}
							getFileUrl={() =>
								getProviderBannerFileUrl({
									payload: { fileExtension: '' },
								})
							}
							aspectRatio="350/145"
							previewHeight={100}
						/>
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="outline" onClick={() => setOpen(false)}>
							ยกเลิก
						</Button>
						<Button onClick={handleSave}>บันทึก</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
